import styled, { css } from 'styled-components';
import { breakpoints, textSize } from '../../styles/brand';

export type TRankingVariant = 'default' | 'board';

export const RankingSection = styled.div<{ $variant: TRankingVariant }>`
  margin-bottom: ${({ $variant }) =>
    $variant === 'board' ? '1.5rem' : '2rem'};
`;

export const RankingTitle = styled.h3<{ $variant: TRankingVariant }>`
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(1.1)};
  color: #323232;
  margin: 0 0 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;

  /* Ícone da prop icon: acompanha o tamanho do título. Sem isso, um SVG sem
     width ocupa a largura toda do título. */
  > svg {
    width: 1.3em;
    height: 1.3em;
    flex: none;
  }

  ${({ $variant }) =>
    $variant === 'board' &&
    css`
      font-size: clamp(1.1rem, 3vh, 2.3rem);
      letter-spacing: -0.01em;
      color: #fff;
      margin-bottom: 0.8vh;
    `}
`;

export const RankingTableWrapper = styled.div<{ $variant: TRankingVariant }>`
  border: 1px solid #e2e2e2;
  border-radius: 0.75rem;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  ${({ $variant }) =>
    $variant === 'board' &&
    css`
      border-color: rgba(255, 255, 255, 0.12);
      background: rgba(255, 255, 255, 0.05);
      border-radius: 1.1rem;
      overflow: hidden;

      /* Celular: se a tabela não couber, rola dentro dela; nenhuma coluna
         fica escondida. */
      @media (max-width: ${breakpoints.md}) {
        overflow-x: auto;
        overflow-y: hidden;
      }
    `}
`;

export const RankingTable = styled.table<{ $variant: TRankingVariant }>`
  width: 100%;
  border-collapse: collapse;
  min-width: 25rem;

  /* Modo compacto da organização (3 colunas): cabe sem rolagem lateral. */
  @media (max-width: ${breakpoints.sm}) {
    min-width: ${({ $variant }) => ($variant === 'default' ? '0' : '25rem')};
  }

  thead {
    background: #fafafa;
    border-bottom: 1px solid #ebebeb;
  }

  th {
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(0.85)};
    color: #555;
    padding: 0.75rem 1rem;
    text-align: center;
    font-weight: normal;
    white-space: nowrap;
  }

  td {
    font-family: 'RedHatDisplay-Regular';
    font-size: ${textSize(0.9)};
    color: #323232;
    padding: 0.7rem 1rem;
    text-align: center;
    border-bottom: 1px solid #f5f5f5;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  tbody tr:hover {
    background: rgba(255, 152, 0, 0.03);
  }

  ${({ $variant }) =>
    $variant === 'board' &&
    css`
      min-width: 0;

      thead {
        background: rgba(255, 255, 255, 0.06);
        border-bottom-color: rgba(255, 255, 255, 0.1);
      }

      th {
        color: rgba(255, 255, 255, 0.72);
        font-size: clamp(0.75rem, 1.7vh, 1.25rem);
        letter-spacing: 0.08em;
        text-transform: uppercase;
        padding: 0.7vh 1.2vw;
      }

      td {
        color: #fff;
        font-size: clamp(1rem, 2.7vh, 2.1rem);
        line-height: 1.12;
        padding: 0.65vh 1.2vw;
        border-bottom-color: rgba(255, 255, 255, 0.07);
      }

      td strong {
        font-family: 'RedHatDisplay-ExtraBold', 'RedHatDisplay-Bold';
        font-variant-numeric: tabular-nums;
        letter-spacing: -0.01em;
      }

      @media (max-width: ${breakpoints.md}) {
        th,
        td {
          padding: 0.55rem 0.7rem;
        }

        th {
          font-size: ${textSize(0.7)};
          letter-spacing: 0.04em;
        }

        td {
          font-size: ${textSize(1.05)};
        }
      }

      tbody tr:hover {
        background: transparent;
      }
    `}
`;

export const TeamCell = styled.td`
  text-align: left !important;
`;

export const SchoolName = styled.span<{ $variant: TRankingVariant }>`
  display: block;
  font-size: 0.75em;
  opacity: ${({ $variant }) => ($variant === 'board' ? 0.65 : 0.7)};
`;

export const MedalCell = styled.td<{
  $position: number;
  $variant: TRankingVariant;
}>`
  font-size: ${({ $position }) => ($position <= 3 ? '1.25rem' : '0.9rem')};
  font-family: 'RedHatDisplay-Bold' !important;
  color: ${({ $position, $variant }) =>
    $position === 1
      ? '#b8860b'
      : $position === 2
        ? '#757575'
        : $position === 3
          ? '#8b4513'
          : $variant === 'board'
            ? '#fff'
            : '#323232'} !important;
  width: 3rem;

  ${({ $variant }) =>
    $variant === 'board' &&
    css`
      font-size: inherit;
      width: 4.5vw;
      min-width: 3.2rem;
    `}
`;

/** Arena da rodada, ao lado da escola. */
export const ArenaName = styled.span`
  white-space: nowrap;
  font-family: 'RedHatDisplay-Bold';
`;

export const NumberCell = styled.td`
  font-variant-numeric: tabular-nums;
`;

export const PendingTag = styled.span<{ $variant: TRankingVariant }>`
  display: inline-block;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  font-family: 'RedHatDisplay-Bold';
  font-size: 0.72em;
  letter-spacing: 0.02em;
  color: ${({ $variant }) => ($variant === 'board' ? '#fde68a' : '#8a6100')};
  background: ${({ $variant }) =>
    $variant === 'board' ? 'rgba(251, 191, 36, 0.18)' : '#fff4d6'};
`;

/** Seta ao lado do nome quando a linha abre detalhes (celular). */
export const ExpandToggle = styled.span<{ $open: boolean }>`
  display: inline-flex;
  margin-left: 0.35rem;
  vertical-align: middle;
  color: #9e9e9e;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 200ms ease;

  svg {
    width: 0.7rem;
    height: 0.7rem;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

export const DetailsRow = styled.tr`
  background: #f6f7f9;

  > td {
    padding: 0.6rem 0.9rem 0.8rem !important;
    text-align: left !important;
  }

  .secondary-button {
    margin-top: 0.6rem;
  }
`;

export const DetailsList = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.5rem 1rem;
  margin: 0;

  div {
    min-width: 0;
  }

  dt {
    font-family: 'RedHatDisplay-Medium';
    font-size: ${textSize(0.7)};
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #676767;
  }

  dd {
    margin: 0.1rem 0 0;
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(0.9)};
    color: #323232;
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
  }
`;
